import { Button, Group, NumberInput, SegmentedControl, Text } from '@mantine/core'
import { useState } from 'react'
import { FaSave } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'

const LevelControl = ({
  label,
  value,
  min,
  max,
  increments,
  defaultIncrement,
  threshold,
  placeholder,
  disabled,
  onCommit,
}: {
  label: string
  value: number | undefined
  min: number
  max: number
  increments: { label: string; value: string }[]
  defaultIncrement: number
  threshold: number
  placeholder: string
  disabled: boolean
  onCommit: (value: number) => void
}) => {
  const { t } = useTranslation(['hardware', 'common'])
  const [increment, setIncrement] = useState<number>(defaultIncrement)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isCleared, setIsCleared] = useState<boolean>(false)
  const [pendingValue, setPendingValue] = useState<number | null>(null)
  const [displaySaveButton, setDisplaySaveButton] = useState<boolean>(false)
  const [seenValue, setSeenValue] = useState(value)

  if (seenValue !== value) {
    setSeenValue(value)
    setIsCleared(false)
  }

  const handleChange = (changed: string | number) => {
    if (changed === '') {
      setPendingValue(null)
      setIsCleared(true)
      setDisplaySaveButton(false)
      return
    }

    const newValue = typeof changed === 'number' ? changed : Number(changed)
    if (Number.isNaN(newValue)) return

    setIsCleared(false)

    if (isEditing) {
      setPendingValue(newValue)
      setDisplaySaveButton(true)
      return
    }

    const baseValue = pendingValue ?? value ?? 0
    if (Math.abs(newValue - baseValue) <= threshold) {
      onCommit(newValue)
      setDisplaySaveButton(false)
      setPendingValue(null)
    } else {
      setDisplaySaveButton(true)
      setPendingValue(newValue)
    }
  }

  const handleSave = () => {
    if (pendingValue === null) return
    onCommit(pendingValue)
    setDisplaySaveButton(false)
    setPendingValue(null)
  }

  return (
    <>
      <Text mb="xs" mt="md">
        {label}
      </Text>
      <Group justify="flex-start" mb="md">
        <SegmentedControl
          value={increment.toString()}
          onChange={selected => setIncrement(parseInt(selected))}
          data={increments}
        />
        <NumberInput
          aria-label={label}
          value={isCleared ? '' : (pendingValue ?? value ?? '')}
          placeholder={placeholder}
          clampBehavior="strict"
          min={min}
          max={max}
          step={increment}
          disabled={disabled}
          onChange={changed => handleChange(changed)}
          onInput={() => setIsEditing(true)}
          onBlur={() => setIsEditing(false)}
        />
        {displaySaveButton && (
          <Button ml="auto" leftSection={<FaSave />} onClick={handleSave}>
            {t('save', { ns: 'common' })}
          </Button>
        )}
      </Group>
    </>
  )
}

export default LevelControl
